import { useState, useEffect } from 'react';
import axiosInstance from '../../api/axios';

const SHIFT_AUTH_OPTIONS = [
    { value: 'SIEMPRE', label: 'Siempre', description: 'Pedir contraseña al abrir y cerrar turno siempre.' },
    { value: 'SOLO_CIERRE', label: 'Solo al Cerrar', description: 'Pedir contraseña solo al cerrar turno.' },
    { value: 'SOLO_ABRIR', label: 'Solo al Abrir', description: 'Pedir contraseña solo al abrir turno.' },
    { value: 'SOLO_DESCUADRE', label: 'Solo si hay Descuadre', description: 'Pedir contraseña al cerrar, solo si hay diferencias en el dinero.' },
    { value: 'NUNCA', label: 'Nunca', description: 'No pedir contraseña en ningún caso.' }
];

export default function ConfiguracionPage() {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [shiftAuthRule, setShiftAuthRule] = useState('SOLO_DESCUADRE');
    const [expensesEnabled, setExpensesEnabled] = useState(true);
    const [expensesOperatorAllowed, setExpensesOperatorAllowed] = useState(false);
    const [expensesLinkedToCashbox, setExpensesLinkedToCashbox] = useState(false);
    const [storeEnabled, setStoreEnabled] = useState(false);
    const [storeOperatorAllowed, setStoreOperatorAllowed] = useState(true);

    useEffect(() => {
        loadSettings();
    }, []);

    const loadSettings = async () => {
        try {
            const response = await axiosInstance.get('/companies/settings');
            if (response.data) {
                if (response.data.shiftAuthRule) {
                    setShiftAuthRule(response.data.shiftAuthRule);
                }
                if (response.data.expensesEnabled !== undefined) {
                    setExpensesEnabled(response.data.expensesEnabled);
                }
                if (response.data.expensesOperatorAllowed !== undefined) {
                    setExpensesOperatorAllowed(response.data.expensesOperatorAllowed);
                }
                if (response.data.expensesLinkedToCashbox !== undefined) {
                    setExpensesLinkedToCashbox(response.data.expensesLinkedToCashbox);
                }
                if (response.data.storeEnabled !== undefined) {
                    setStoreEnabled(response.data.storeEnabled);
                }
                if (response.data.storeOperatorAllowed !== undefined) {
                    setStoreOperatorAllowed(response.data.storeOperatorAllowed);
                }
            }
        } catch (error) {
            console.error("Error loading settings:", error);
        } finally {
            setLoading(false);
        }
    }

    const handleSave = async () => {
        setSaving(true);
        try {
            await axiosInstance.put('/companies/settings', {
                shiftAuthRule,
                expensesOperatorAllowed,
                expensesLinkedToCashbox,
                ...(storeEnabled ? { storeOperatorAllowed } : {}),
            });
            alert('Configuración guardada exitosamente');
        } catch (error) {
            console.error("Error saving settings:", error);
            alert('Error al guardar la configuración');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center p-12">
                <i className="ri-loader-4-line animate-spin text-4xl text-teal-600"></i>
            </div>
        );
    }

    return (
            <div className="max-w-4xl mx-auto space-y-6">
                
                {/* Shift Configuration Card */}
                <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
                    <div className="flex items-start gap-4 mb-6">
                        <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center text-blue-600">
                            <i className="ri-secure-payment-line text-2xl"></i>
                        </div>
                        <div>
                            <h2 className="text-lg font-bold text-slate-800">Autorización de Turnos</h2>
                            <p className="text-slate-500 text-sm">
                                Configure cuándo se requiere la autorización (contraseña de administrador) para las operaciones de caja.
                            </p>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {SHIFT_AUTH_OPTIONS.map((option) => (
                            <div 
                                key={option.value}
                                onClick={() => setShiftAuthRule(option.value)}
                                className={`
                                    relative p-4 rounded-xl border-2 cursor-pointer transition-all hover:shadow-md
                                    ${shiftAuthRule === option.value 
                                        ? 'border-teal-500 bg-teal-50' 
                                        : 'border-slate-100 bg-white hover:border-slate-200'
                                    }
                                `}
                            >
                                <div className="flex items-center justify-between mb-2">
                                    <span className={`font-bold ${shiftAuthRule === option.value ? 'text-teal-800' : 'text-slate-700'}`}>
                                        {option.label}
                                    </span>
                                    {shiftAuthRule === option.value && (
                                        <i className="ri-checkbox-circle-fill text-teal-500 text-xl"></i>
                                    )}
                                </div>
                                <p className={`text-xs ${shiftAuthRule === option.value ? 'text-teal-600' : 'text-slate-400'}`}>
                                    {option.description}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Operator Permissions Card - Solo si el módulo está habilitado por SUPERADMIN */}
                {expensesEnabled && (
                    <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 space-y-4">
                        <div className="flex items-start gap-4">
                            <div className="w-12 h-12 bg-purple-50 rounded-xl flex items-center justify-center text-purple-600">
                                <i className="ri-user-settings-line text-2xl"></i>
                            </div>
                            <div>
                                <h2 className="text-lg font-bold text-slate-800">Permisos de Gastos</h2>
                                <p className="text-slate-500 text-sm">
                                    Configura las reglas para el módulo de gastos.
                                </p>
                            </div>
                        </div>

                        {/* Operadores */}
                        <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                            <div>
                                <p className="font-bold text-slate-700">Permitir operadores usar gastos</p>
                                <p className="text-xs text-slate-500">Si está desactivado, solo los administradores tendrán acceso</p>
                            </div>
                            <label className="relative inline-flex items-center cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={expensesOperatorAllowed}
                                    onChange={(e) => setExpensesOperatorAllowed(e.target.checked)}
                                    className="sr-only peer"
                                />
                                <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                            </label>
                        </div>

                        {/* Vincular a Caja */}
                        <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                            <div>
                                <p className="font-bold text-slate-700">Vincular gastos a la caja diaria</p>
                                <p className="text-xs text-slate-500">Los gastos en efectivo se descontarán del reporte de caja (Turnos)</p>
                            </div>
                            <label className="relative inline-flex items-center cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={expensesLinkedToCashbox}
                                    onChange={(e) => setExpensesLinkedToCashbox(e.target.checked)}
                                    className="sr-only peer"
                                />
                                <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                            </label>
                        </div>
                    </div>
                )}

                {/* Tienda - Solo si el módulo está habilitado por SUPERADMIN */}
                {storeEnabled && (
                    <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 space-y-4">
                        <div className="flex items-start gap-4">
                            <div className="w-12 h-12 bg-purple-50 rounded-xl flex items-center justify-center text-purple-600">
                                <i className="ri-store-2-line text-2xl"></i>
                            </div>
                            <div>
                                <h2 className="text-lg font-bold text-slate-800">Tienda</h2>
                                <p className="text-slate-500 text-sm">
                                    Las ventas de la tienda entran a la caja del turno y descuentan el inventario de la sede.
                                </p>
                            </div>
                        </div>

                        <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                            <div>
                                <p className="font-bold text-slate-700">Permitir operadores vender productos</p>
                                <p className="text-xs text-slate-500">Si está desactivado, solo los administradores podrán registrar ventas</p>
                            </div>
                            <label className="relative inline-flex items-center cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={storeOperatorAllowed}
                                    onChange={(e) => setStoreOperatorAllowed(e.target.checked)}
                                    className="sr-only peer"
                                />
                                <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                            </label>
                        </div>
                    </div>
                )}

                {/* Save Button */}
                <div className="flex justify-end pt-4">
                    <button 
                        onClick={handleSave}
                        disabled={saving}
                        className="bg-slate-900 text-white px-8 py-3 rounded-xl font-bold hover:bg-slate-800 transition shadow-lg shadow-slate-200 disabled:opacity-50 flex items-center gap-2"
                    >
                        {saving && <i className="ri-loader-4-line animate-spin"></i>}
                        {saving ? 'Guardando...' : 'Guardar Cambios'}
                    </button>
                </div>

            </div>
    );
}
